/**
 * /reports/:reportId — Report viewer.
 * Shows KPI tiles, optional chart placeholder, and the full data table.
 * Params panel on the right lets users re-run with different values.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { reportDetailQueryOptions, type ReportRunResult } from "@/lib/queries";
import { api } from "@/lib/api";
import { setRouteLabel } from "@/lib/navHistory";
import { ProjectSelect } from "@/components/ProjectSelect";
import { ProjectMultiSelect } from "@/components/ProjectMultiSelect";
import type { ProjectSummary } from "@/lib/types";

type DslParam = {
  name: string;
  label: string;
  type?: string;
  default?: string | string[] | null;
  required?: boolean;
};

export const Route = createFileRoute("/_auth/reports/$reportId")({
  component: ReportViewerPage,
});

function KpiTile({ label, value, format }: { label: string; value: unknown; format: string }) {
  const display =
    format === "currency"
      ? typeof value === "number"
        ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value)
        : String(value ?? "—")
      : format === "percent"
        ? typeof value === "number"
          ? `${(value * 100).toFixed(1)}%`
          : String(value ?? "—")
        : String(value ?? "—");

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-slate-200 dark:border-gray-800 px-4 py-3">
      <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
      <p className="text-xl font-bold text-slate-900 dark:text-slate-100 mt-0.5">{display}</p>
    </div>
  );
}

type SelectCol = { as: string; expr: string; type: string; drillable?: boolean };

function fmtCellValue(value: unknown, type: string): string {
  if (value === null || value === undefined) return "—";
  if (type === "currency" && typeof value === "number")
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
  if (type === "percent" && typeof value === "number") return `${(value * 100).toFixed(2)}%`;
  if (type === "date" || type === "month") return String(value);
  return String(value);
}

function DataTable({
  columns,
  rows,
  drillableNames,
  onDrill,
}: {
  columns: { name: string; type: string }[];
  rows: Record<string, unknown>[];
  drillableNames?: Set<string>;
  onDrill?: (row: Record<string, unknown>, columnName: string) => void;
}) {
  if (rows.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500 dark:text-slate-400 text-sm">
        No rows returned for the selected parameters.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 dark:border-gray-700">
            {columns.map((col) => (
              <th
                key={col.name}
                className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap"
              >
                {col.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            // Financial statements (QuickBooks layout) mark each row's role in
            // _kind: "section" / "header" titles, "subtotal" / "total" /
            // "result" lines, "synthetic" lines with no account behind them
            // (Current Year Earnings) and "account" rows. Only account rows
            // have journal lines to drill into. _depth indents sub-accounts.
            const kind = typeof row._kind === "string" ? row._kind : undefined;
            const depth = typeof row._depth === "number" ? row._depth : 0;
            const rowDrillable = kind === undefined || kind === "account";
            const rowStyle =
              kind === "section"
                ? "font-semibold text-slate-900 dark:text-slate-100"
                : kind === "header"
                  ? "font-medium"
                  : kind === "subtotal"
                    ? "font-semibold"
                    : kind === "total" || kind === "result"
                      ? "font-semibold bg-slate-50/60 dark:bg-gray-800/30"
                      : "";
            return (
            <tr
              key={i}
              className={`border-b border-slate-100 dark:border-gray-800 hover:bg-slate-50 dark:hover:bg-gray-800/50 ${rowStyle}`}
            >
              {columns.map((col) => {
                const key = col.name.replace(/ /g, "_").replace(/\//g, "_");
                const val = row[col.name] ?? row[key];
                const isDrillable = rowDrillable && drillableNames?.has(col.name) && onDrill;
                const indent = col.name === "Account Name" && depth > 0 ? { paddingLeft: `${0.75 + depth * 1.25}rem` } : undefined;
                // Statement title rows have no amount: leave the cell blank.
                const blank = kind !== undefined && (val === null || val === undefined);
                return (
                  <td
                    key={col.name}
                    style={indent}
                    className={`py-2.5 px-3 whitespace-nowrap ${
                      isDrillable
                        ? "text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                        : "text-slate-700 dark:text-slate-300"
                    }`}
                    onClick={isDrillable ? () => onDrill!(row, col.name) : undefined}
                    title={isDrillable ? "Click to drill down" : undefined}
                  >
                    {blank ? "" : fmtCellValue(val, col.type)}
                  </td>
                );
              })}
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Where a report's record opens, by the report's `from` entity. A drill into
// one of these shows an Open link per record (the row's hidden _row_id).
type RecordRoute = { noun: string; path: (id: string) => string };
const RECORD_ROUTES: Record<string, RecordRoute> = {
  invoices: { noun: "invoice", path: (id) => `/invoices/ar/${id}` },
  ap_invoices: { noun: "bill", path: (id) => `/invoices/ap/${id}` },
  journals: { noun: "journal", path: (id) => `/journals/${id}` },
  projects: { noun: "project", path: (id) => `/projects/${id}` },
  customers: { noun: "customer", path: (id) => `/customers/${id}` },
  vendors: { noun: "vendor", path: (id) => `/vendors/${id}` },
};

function recordIdOf(row: Record<string, unknown>): string | null {
  const v = row._row_id ?? row.ID ?? row.id;
  return typeof v === "string" && v ? v : null;
}

function DrillPanel({
  title,
  result,
  loading,
  error,
  onClose,
  recordRoute,
}: {
  title: string;
  result: ReportRunResult | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  recordRoute?: RecordRoute;
}) {
  return (
    <div className="fixed inset-0 z-40" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="absolute right-0 top-0 bottom-0 w-[640px] bg-white dark:bg-gray-900 border-l border-slate-200 dark:border-gray-800 shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 dark:border-gray-800 flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">Drill into</p>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 truncate">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-xl leading-none px-2"
            aria-label="Close drill"
          >
            ×
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <div className="text-sm text-slate-500 dark:text-slate-400 py-8 text-center">
              Loading underlying records…
            </div>
          )}
          {error && (
            <div className="text-sm text-red-500 bg-red-50 dark:bg-red-950/30 p-3 rounded">
              {error}
            </div>
          )}
          {result && !loading && (
            <>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                {result.row_count.toLocaleString()} underlying rows · {result.duration_ms}ms
              </p>
              <DataTable columns={result.columns} rows={result.rows} />
              {recordRoute && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {result.rows.map((r, i) => {
                    const id = recordIdOf(r);
                    if (!id) return null;
                    const first = result.columns[0]?.name ?? "";
                    const shown = r[first] ?? r[first.replace(/ /g, "_")];
                    return (
                      <Link
                        key={`${id}-${i}`}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        to={recordRoute.path(id) as any}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                      >
                        Open {recordRoute.noun}
                        {result.rows.length > 1 && shown != null ? ` ${String(shown)}` : ""}
                      </Link>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Symbolic defaults ("today", "fiscal_year_start", ...) are resolved by the
// server from the company's own fiscal year; the run result's params_used
// carries the dates it chose, which then fill the form.
const SERVER_DEFAULTS = new Set([
  "today",
  "fiscal_year_start",
  "fiscal_year_end",
  "fiscal_month_start",
  "current_period_start",
  "current_period_end",
]);

function isServerDefault(spec: DslParam): boolean {
  return typeof spec.default === "string" && SERVER_DEFAULTS.has(spec.default);
}

function ParamField({
  spec,
  value,
  onChange,
}: {
  spec: DslParam;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const label = (
    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
      {spec.label}
    </label>
  );

  if (spec.type === "project_id") {
    return (
      <div>
        {label}
        <ProjectSelect
          value={(value as ProjectSummary | null) ?? null}
          onChange={(v) => onChange(v)}
          label=""
          required={spec.required}
          showStatusDropdown={false}
        />
      </div>
    );
  }

  if (spec.type === "project_ids") {
    return (
      <div>
        {label}
        <ProjectMultiSelect
          value={(value as ProjectSummary[]) ?? []}
          onChange={(v) => onChange(v)}
          label=""
          required={spec.required}
          showStatusDropdown={false}
        />
      </div>
    );
  }

  // Date / daterange / string / int / decimal / currency / etc. → plain input
  const inputType = spec.type === "date" || spec.name.includes("date") ? "date" : "text";
  const stringValue =
    typeof value === "string"
      ? value
      : typeof spec.default === "string" && !isServerDefault(spec)
        ? spec.default
        : "";

  return (
    <div>
      {label}
      <input
        type={inputType}
        value={stringValue}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-2.5 py-1.5 text-sm border border-slate-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100"
      />
    </div>
  );
}

function ReportViewerPage() {
  const { reportId } = Route.useParams();
  const { data: report, isLoading: loadingDef } = useQuery(reportDetailQueryOptions(reportId));
  const [result, setResult] = useState<ReportRunResult | null>(null);
  // Param values can be strings (date/text), objects (single project), or arrays
  // (multi project). Serialised to wire format in `paramsForWire` below.
  const [params, setParams] = useState<Record<string, unknown>>({});
  const [drillState, setDrillState] = useState<
    | { open: true; title: string; row: Record<string, unknown>; column: string }
    | { open: false }
  >({ open: false });

  // Convert UI param values to the wire shape: project objects → ids, project
  // lists → list of ids, everything else passthrough. Empty values (including
  // empty arrays) are dropped so optional `IN (...)` clauses are skipped
  // server-side rather than producing invalid `IN ()` SQL.
  const paramsForWire = useMemo(() => {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(params)) {
      if (v === "" || v === null || v === undefined) continue;
      if (Array.isArray(v)) {
        if (v.length === 0) continue;
        out[k] = v.map((item) =>
          item && typeof item === "object" && "id" in item
            ? (item as { id: string }).id
            : item,
        );
      } else if (v && typeof v === "object" && "id" in v) {
        out[k] = (v as { id: string }).id;
      } else {
        out[k] = v;
      }
    }
    return out;
  }, [params]);

  const runMutation = useMutation({
    mutationFn: (previewOnly: boolean) =>
      api<ReportRunResult>(`/api/reports/${reportId}/${previewOnly ? "preview" : "run"}`, {
        method: "POST",
        body: JSON.stringify({ params: paramsForWire }),
      }),
    onSuccess: (data) => {
      setResult(data);
      // Show the dates the server resolved for server-side defaults (e.g. the
      // fiscal year start) in any field the user left empty.
      const used = data.params_used ?? {};
      setParams((prev) => {
        const next = { ...prev };
        for (const p of dslParams) {
          const v = used[p.name];
          if ((next[p.name] === undefined || next[p.name] === "") && typeof v === "string") {
            next[p.name] = v.slice(0, 10);
          }
        }
        return next;
      });
    },
  });

  const drillMutation = useMutation({
    mutationFn: (vars: { row: Record<string, unknown>; column: string }) =>
      api<ReportRunResult>(`/api/reports/${reportId}/drill`, {
        method: "POST",
        body: JSON.stringify({ row: vars.row, column: vars.column, params: paramsForWire }),
      }),
  });

  // Recent pages and the chat's context name the report, not its id (FIX-LOG 110).
  useEffect(() => {
    if (report?.name) setRouteLabel(`/reports/${reportId}`, report.name);
  }, [report?.name, reportId]);

  const dsl = report?.dsl as Record<string, unknown> | undefined;
  const dslParams = (dsl?.params as DslParam[]) ?? [];
  const dslSelect = (dsl?.select as SelectCol[] | undefined) ?? [];
  const drillableNames = new Set(dslSelect.filter((c) => c.drillable).map((c) => c.as));

  // Land on a populated grid, not a blank "Run" form. When the report loads,
  // seed any param defaults into state, then auto-run once. This is what the
  // user expects when they navigate here from the agent ("show me overdue
  // bills") or the guided tour — the data should already be on screen.
  const didAutoRun = useRef(false);
  useEffect(() => {
    if (!report) return;
    // Seed param defaults into state so the auto-run uses them.
    setParams((prev) => {
      const next = { ...prev };
      for (const p of dslParams) {
        if (next[p.name] !== undefined) continue;
        if (isServerDefault(p)) {
          // Left empty: the server resolves it and the run fills it in.
          continue;
        } else if (typeof p.default === "string" && p.default) {
          next[p.name] = p.default;
        } else if (p.type === "date" || p.name.includes("date")) {
          // Date params with no default → today, so as-of / aging reports
          // run for the current date instead of erroring on a missing date.
          next[p.name] = new Date().toISOString().slice(0, 10);
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  useEffect(() => {
    if (!report || didAutoRun.current || result) return;
    // Don't auto-run if a required param still has no value (defaults may not
    // have seeded yet, or the user genuinely must choose) — leave the form.
    const missingRequired = dslParams.some(
      (p) =>
        p.required &&
        !isServerDefault(p) &&
        (paramsForWire[p.name] === undefined || paramsForWire[p.name] === ""),
    );
    if (missingRequired) return;
    didAutoRun.current = true;
    runMutation.mutate(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, paramsForWire]);

  const openDrill = (row: Record<string, unknown>, column: string) => {
    setDrillState({ open: true, title: column, row, column });
    drillMutation.reset();
    drillMutation.mutate({ row, column });
  };
  const closeDrill = () => setDrillState({ open: false });

  if (loadingDef) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500 dark:text-slate-400 text-sm">
        Loading…
      </div>
    );
  }

  if (!report) {
    return (
      <div className="p-8 text-center text-slate-500">
        Report not found.{" "}
        <Link to="/reports" className="text-blue-600 hover:underline">
          Back to reports
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 dark:bg-gray-900 dark:border-gray-800 flex-shrink-0">
        <div className="flex items-center gap-3">
          <Link to="/reports" className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 text-sm">
            ← Reports
          </Link>
          <span className="text-slate-300 dark:text-slate-700">/</span>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            {report.name}
          </h1>
          {report.ai_built && (
            <span className="inline-flex items-center gap-1 text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded-full">
              ✨ AI built
            </span>
          )}
        </div>
        {report.description && (
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{report.description}</p>
        )}
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Main content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 bg-slate-50 dark:bg-gray-950">
          {/* KPI tiles */}
          {result && result.kpis.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              {result.kpis.map((kpi) => (
                <KpiTile key={kpi.label} label={kpi.label} value={kpi.value} format={kpi.format} />
              ))}
            </div>
          )}

          {/* Empty state / run prompt */}
          {!result && (
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-slate-200 dark:border-gray-800 p-12 text-center">
              <p className="text-4xl mb-3">📊</p>
              <p className="font-medium text-slate-700 dark:text-slate-300">Set parameters and run the report</p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Configure the filters on the right, then click Run.
              </p>
            </div>
          )}

          {/* Results table */}
          {result && (
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-slate-200 dark:border-gray-800 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 dark:border-gray-800 flex items-center justify-between">
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  {result.row_count.toLocaleString()} rows · {result.duration_ms}ms
                  {result.cached && <span className="ml-2 text-xs text-blue-500">cached</span>}
                </span>
              </div>
              <DataTable
                columns={result.columns}
                rows={result.rows}
                drillableNames={drillableNames}
                onDrill={openDrill}
              />
            </div>
          )}
        </div>

        {/* Params panel */}
        <div className="w-64 flex-shrink-0 bg-white dark:bg-gray-900 border-l border-slate-200 dark:border-gray-800 overflow-y-auto flex flex-col">
          <div className="px-4 py-4 border-b border-slate-100 dark:border-gray-800">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Parameters
            </p>
          </div>
          <div className="flex-1 px-4 py-4 space-y-4">
            {dslParams.length === 0 && (
              <p className="text-xs text-slate-400 dark:text-slate-600">No parameters for this report.</p>
            )}
            {dslParams.map((p) => (
              <ParamField
                key={p.name}
                spec={p}
                value={params[p.name]}
                onChange={(v) => setParams((prev) => ({ ...prev, [p.name]: v }))}
              />
            ))}
          </div>
          <div className="px-4 py-4 border-t border-slate-100 dark:border-gray-800 space-y-2">
            <button
              onClick={() => runMutation.mutate(false)}
              disabled={runMutation.isPending}
              className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition"
            >
              {runMutation.isPending ? "Running…" : "▶ Run"}
            </button>
            <button
              onClick={() => runMutation.mutate(true)}
              disabled={runMutation.isPending}
              className="w-full px-3 py-2 bg-slate-100 dark:bg-gray-800 hover:bg-slate-200 dark:hover:bg-gray-700 text-slate-700 dark:text-slate-300 text-sm rounded-lg transition"
            >
              Preview (100 rows)
            </button>
          </div>
        </div>
      </div>

      {drillState.open && (
        <DrillPanel
          title={drillState.title}
          result={drillMutation.data ?? null}
          loading={drillMutation.isPending}
          error={
            drillMutation.error
              ? (drillMutation.error as { data?: { detail?: string }; message?: string }).data?.detail ??
                (drillMutation.error as Error).message ??
                "Drill failed"
              : null
          }
          onClose={closeDrill}
          recordRoute={RECORD_ROUTES[(dsl?.from as string | undefined) ?? ""]}
        />
      )}
    </div>
  );
}
